/**
 * Cloudflare Turnstile widget (T27.2.10.1 Phase B).
 *
 * Renders the bot-challenge widget on the Create account screen and reports the
 * solved token up via `onToken`. The site key is public (`VITE_TURNSTILE_SITE_KEY`,
 * baked into the web build); the SECRET key is server-side only and never reaches
 * the client — the backend verifies the token with it.
 *
 * Loads the Turnstile script once and reuses it across mounts, then explicitly
 * renders into a ref'd div so React owns the lifecycle. On token expiry the
 * widget clears the token (the form re-disables submit until re-solved). When
 * `VITE_TURNSTILE_SITE_KEY` is unset (a local stand or the Capacitor Android
 * build without a key), the widget renders a small notice and reports a
 * non-empty placeholder token (`UNCONFIGURED_TURNSTILE_TOKEN`). The placeholder
 * is non-empty on purpose: the backend's `registerSchema` requires a non-empty
 * `turnstileToken` (an absent/empty token is a 400 before any bypass runs), and
 * the off-production secret-verification bypass then accepts the placeholder
 * without calling Cloudflare. A real build has the site key, so this path never
 * runs — the live widget always produces a real solved token.
 */

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Placeholder token sent when no site key is configured. Non-empty so the
 * backend's `registerSchema` (turnstileToken min length 1) accepts it; the
 * off-production secret-verification bypass then lets it through without
 * contacting Cloudflare. A real build never reaches this — the live widget
 * produces a real token.
 */
export const UNCONFIGURED_TURNSTILE_TOKEN = 'unconfigured';

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
}
interface TurnstileApi {
  render: (el: HTMLElement, options: TurnstileRenderOptions) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const noticeStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

export interface TurnstileWidgetProps {
  /** Called with the solved token (or '' on expiry / error). */
  onToken: (token: string) => void;
}

export function TurnstileWidget({ onToken }: TurnstileWidgetProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [scriptError, setScriptError] = useState(false);

  const siteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim();

  useEffect(() => {
    // No site key configured (local stand / Capacitor build): nothing to render.
    // Emit a non-empty placeholder — the backend requires a non-empty token, and
    // its off-production bypass accepts the placeholder without verifying it.
    if (!siteKey) {
      onToken(UNCONFIGURED_TURNSTILE_TOKEN);
      return;
    }

    let cancelled = false;

    const renderWidget = (): void => {
      if (cancelled || !window.turnstile || !containerRef.current) return;
      if (widgetIdRef.current) return; // already rendered
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme: 'auto',
        callback: (token: string) => onToken(token),
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
      if (existing) {
        existing.addEventListener('load', renderWidget);
      } else {
        const script = document.createElement('script');
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = TURNSTILE_SRC;
        script.async = true;
        script.defer = true;
        script.onload = renderWidget;
        script.onerror = () => {
          if (!cancelled) setScriptError(true);
        };
        document.head.appendChild(script);
      }
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore — the widget may already be gone on unmount
        }
        widgetIdRef.current = null;
      }
    };
    // onToken is a stable callback from the parent (memoised); siteKey is build-
    // time constant. Deps kept minimal so the widget isn't re-rendered.
  }, [siteKey, onToken]);

  if (!siteKey) {
    return (
      <p style={noticeStyle} data-testid="turnstile-unconfigured">
        Bot protection is not configured for this environment — continuing without
        it.
      </p>
    );
  }

  if (scriptError) {
    return (
      <p role="alert" style={noticeStyle} data-testid="turnstile-error">
        Could not load the bot-protection challenge. Refresh and try again.
      </p>
    );
  }

  return <div ref={containerRef} data-testid="turnstile-widget" />;
}
